import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { MapPin } from 'lucide-react'

import { readDraft } from './signupState'
import { subscribeSetupCompletingTransition } from './setupCompletingTransitionController'

/**
 * SetupCompletingTransition — the 2-second "you're ready" climax that
 * plays between the final wizard step's Continue (currently policies;
 * was branding before that step was retired) and the /community/welcome
 * page. Three-beat sequence:
 *
 *   t=0     "Setting up your portal." | ring + logo fade in | ring
 *           begins filling (linear, 1900ms total)
 *   t=700   "Adding your venues." | venue thumbnails cascade in
 *           (250ms each, 80ms stagger, starting at t=750)
 *   t=1400  "You're ready."
 *   t=1750  Ring completion pulse (scale 1→1.04→1, brightness bump)
 *   t=1900  Ring reaches 100%
 *   t=2000  Whole loader fades out (400ms)
 *   t=2400  onComplete() — loader unmounts
 *
 * The wizard → welcome navigation has already fired by the time this
 * mounts: the loader sits portal'd to document.body with an opaque
 * background, hiding the boundary fade-out/fade-in playing beneath.
 *
 * Mount point: the SetupCompletingTransitionMount component (default
 * export) is rendered once at App.jsx as a sibling to <Routes>. It
 * subscribes to the controller pub/sub and toggles its own visibility,
 * so the loader can outlive any route change. Under reduced motion it
 * skips rendering entirely — the user lands on /community/welcome
 * directly via the boundary transition.
 */

const LOGO_STORAGE_KEY = 'statbox.community.logo.v1'
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1]

const RING_SIZE = 140
const RING_STROKE = 3
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2 // keeps stroke inside the viewBox
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const T = {
  beat2: 700,        // ms — text crossfade to "Adding your venues."
  beat3: 1400,       // ms — text crossfade to "You're ready."
  pulse: 1750,       // ms — ring completion pulse
  fadeOutStart: 2000,
  unmount: 2400,
}

const RING_FILL_DURATION_S = 1.9    // total ring fill, linear
const RING_FADE_IN_DURATION_S = 0.3 // ring + logo fade in
const FADE_OUT_DURATION_S = 0.4

function readLogoFromStorage() {
  try { return localStorage.getItem(LOGO_STORAGE_KEY) || '' } catch { return '' }
}

function readVenuesFromDraft() {
  try {
    const draft = readDraft()
    const v = draft.setupWizard?.venues
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

/* ─────────────── Loader ─────────────── */

function SetupCompletingTransition({ onComplete }) {
  const reduceMotion = useReducedMotion()

  // Snapshot data on mount — the wizard's sessionStorage may be cleared
  // or mutated by the Welcome page mounting underneath, and the loader
  // should never visibly change after it starts.
  const [logoDataUrl] = useState(readLogoFromStorage)
  const [venues] = useState(readVenuesFromDraft)

  const [beatIdx, setBeatIdx] = useState(0)
  const [pulse, setPulse] = useState(false)
  const [fading, setFading] = useState(false)

  const middleBeatText = venues.length > 0
    ? 'Adding your venues.'
    : 'Preparing your portal.'
  const beatTexts = [
    'Setting up your portal.',
    middleBeatText,
    "You're ready.",
  ]

  // Thumbnails: max 4 visible. With >4 venues, show 3 + a "+N more" chip.
  const showOverflow = venues.length > 4
  const visibleVenues = showOverflow ? venues.slice(0, 3) : venues
  const overflowCount = venues.length - 3

  // Orchestration timeline. Under reduced motion we skip the whole
  // animation and fire onComplete on the next tick so the navigate()
  // that just landed on /community/welcome gets to settle without a
  // visible flash from the loader. (We still render once so the
  // exit unmount goes through the normal AnimatePresence path.)
  useEffect(() => {
    if (reduceMotion) {
      const t = setTimeout(() => onComplete?.(), 0)
      return () => clearTimeout(t)
    }
    const timers = [
      setTimeout(() => setBeatIdx(1), T.beat2),
      setTimeout(() => setBeatIdx(2), T.beat3),
      setTimeout(() => setPulse(true), T.pulse),
      setTimeout(() => setFading(true), T.fadeOutStart),
      setTimeout(() => onComplete?.(), T.unmount),
    ]
    return () => timers.forEach(clearTimeout)
  }, [reduceMotion, onComplete])

  if (reduceMotion) return null

  const containerStagger = {
    hidden: {},
    show: {
      transition: {
        delayChildren: 0.75,
        staggerChildren: 0.08,
      },
    },
  }
  const itemFadeUp = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: EASE_OUT_EXPO } },
  }

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center font-sans bg-[var(--signup-bg-base)] text-[var(--signup-text-primary)]"
      initial={{ opacity: 0 }}
      animate={{ opacity: fading ? 0 : 1 }}
      transition={{
        duration: fading ? FADE_OUT_DURATION_S : 0.2,
        ease: EASE_OUT_EXPO,
      }}
      role="status"
      aria-live="polite"
      aria-label="Finishing setup"
    >
      <div className="flex flex-col items-center w-full max-w-[480px] px-6">
        {/* Ring + logo (or fallback logomark) */}
        <motion.div
          className="relative"
          style={{ width: RING_SIZE, height: RING_SIZE }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            scale: pulse ? [1, 1.04, 1] : 1,
            filter: pulse
              ? ['brightness(1)', 'brightness(1.15)', 'brightness(1)']
              : 'brightness(1)',
          }}
          transition={{
            opacity: { duration: RING_FADE_IN_DURATION_S, ease: EASE_OUT_EXPO },
            scale: pulse ? { duration: 0.25, ease: 'easeInOut' } : { duration: 0 },
            filter: pulse ? { duration: 0.25, ease: 'easeInOut' } : { duration: 0 },
          }}
        >
          <svg
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            className="absolute inset-0"
            aria-hidden
          >
            {/* Background track — full circle, low opacity */}
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="var(--signup-border-subtle, #e5e5e5)"
              strokeWidth={1.5}
              opacity={0.6}
            />
            {/* Progress fill — animates strokeDashoffset C → 0 */}
            <motion.circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="var(--signup-accent)"
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              initial={{ strokeDashoffset: RING_CIRCUMFERENCE }}
              animate={{ strokeDashoffset: 0 }}
              transition={{ duration: RING_FILL_DURATION_S, ease: 'linear' }}
            />
          </svg>

          {/* Logo inside the ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            {logoDataUrl ? (
              <div
                className="h-20 w-20 rounded-xl bg-white border border-[var(--signup-border-subtle)] flex items-center justify-center overflow-hidden"
                style={{ boxShadow: 'var(--signup-shadow-sm)' }}
              >
                <img
                  src={logoDataUrl}
                  alt=""
                  aria-hidden
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <div
                className="h-20 w-20 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: 'var(--signup-accent)',
                  fontWeight: 700,
                  fontSize: 36,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}
                aria-hidden
              >
                S
              </div>
            )}
          </div>
        </motion.div>

        {/* Venue thumbnails — only render the row at all if we have any. */}
        {venues.length > 0 && (
          <motion.div
            className="mt-8 flex items-center gap-2"
            variants={containerStagger}
            initial="hidden"
            animate="show"
            aria-hidden
          >
            {visibleVenues.map((v) => (
              <VenueThumb key={v.placeId} venue={v} variants={itemFadeUp} />
            ))}
            {showOverflow && (
              <motion.div
                variants={itemFadeUp}
                className="h-10 px-2.5 min-w-[40px] rounded-md bg-[var(--signup-bg-subtle)] flex items-center justify-center text-[12px] font-medium text-[var(--signup-text-secondary)]"
              >
                +{overflowCount} more
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Beat text — crossfade between values, fixed-height row to
            prevent layout shift while old/new overlap. */}
        <div
          className={`relative ${venues.length > 0 ? 'mt-8' : 'mt-8'} h-6 w-full`}
        >
          <AnimatePresence>
            <motion.p
              key={beatIdx}
              className="absolute inset-0 flex items-center justify-center text-[16px] font-medium text-[var(--signup-text-primary)]"
              style={{ letterSpacing: '-0.005em' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.25, delay: 0.05, ease: EASE_OUT_EXPO },
              }}
            >
              {beatTexts[beatIdx]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

function VenueThumb({ venue, variants }) {
  const [errored, setErrored] = useState(false)
  const showPhoto = !!venue.photoUrl && !venue.isManual && !errored

  return (
    <motion.div
      variants={variants}
      className="h-10 w-10 rounded-md overflow-hidden bg-[var(--signup-bg-subtle)] flex items-center justify-center"
    >
      {showPhoto ? (
        <img
          src={venue.photoUrl}
          alt=""
          aria-hidden
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        <MapPin
          size={18}
          strokeWidth={1.75}
          className="text-[var(--signup-text-tertiary)]"
          style={{ opacity: 0.4 }}
        />
      )}
    </motion.div>
  )
}

/* ─────────────── Top-level mount point ─────────────── */

/**
 * Rendered once in App.jsx as a sibling to <Routes>. Subscribes to the
 * setupCompletingTransition controller and toggles visibility on
 * trigger; the loader's onComplete clears the flag so it unmounts.
 *
 * Under reduced motion the trigger is a no-op — there's no animated
 * climax to play, and the boundary fade-in to /community/welcome
 * already gives users a clean state change.
 */
export default function SetupCompletingTransitionMount() {
  const reduceMotion = useReducedMotion()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    return subscribeSetupCompletingTransition(() => {
      if (reduceMotion) return
      setVisible(true)
    })
  }, [reduceMotion])

  // Portal the AnimatePresence into document.body so the loader sits
  // above every route regardless of the current React tree shape.
  return createPortal(
    <AnimatePresence>
      {visible && (
        <SetupCompletingTransition
          key="setup-completing"
          onComplete={() => setVisible(false)}
        />
      )}
    </AnimatePresence>,
    document.body,
  )
}
